"use client";

import { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import axiosInstance from "../../../utils/axiosInstance";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { API_ENDPOINTS } from "../../../constants/config";

const EditStaffModal = ({ isOpen, onClose, staff, onUpdate }) => {
  const [formData, setFormData] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    staffId: "",
    email: "",
    phoneNumber: "",
    birthday: "",
    bloodGroup: "",
    gender: "",
    address: {
      street: "",
      city: "",
      state: "",
      pincode: "",
      country: "India",
    },
    // Staff-specific fields
    dateOfJoining: "",
    maritalStatus: "",
    panNumber: "",
    epf: "",
    basicSalary: "",
    allowance: "",
    teacherType: "",
    contractType: "",
    status: "active",
    bankAccountDetails: {
      accountHolderName: "",
      accountNumber: "",
      bankName: "",
      ifsc: "",
    },
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (staff) {
      setFormData({
        firstName: staff.firstName || "",
        middleName: staff.middleName || "",
        lastName: staff.lastName || "",
        staffId: staff.staffId || "",
        email: staff.email || "",
        phoneNumber: staff.phoneNumber || "",
        birthday: staff.birthday
          ? new Date(staff.birthday).toISOString().split("T")[0]
          : "",
        bloodGroup: staff.bloodGroup || "",
        gender: staff.gender || "",
        address: {
          street: staff.address?.street || "",
          city: staff.address?.city || "",
          state: staff.address?.state || "",
          pincode: staff.address?.pincode || "",
          country: staff.address?.country || "India",
        },
        // Staff-specific fields
        dateOfJoining: staff.dateOfJoining
          ? new Date(staff.dateOfJoining).toISOString().split("T")[0]
          : "",
        maritalStatus: staff.maritalStatus || "",
        panNumber: staff.panNumber || "",
        epf: staff.epf || "",
        basicSalary: staff.basicSalary || "",
        allowance: staff.allowance || "",
        teacherType: staff.teacherType || "",
        contractType: staff.contractType || "",
        status: staff.status || "active",
        bankAccountDetails: {
          accountHolderName: staff.bankAccountDetails?.accountHolderName || "",
          accountNumber: staff.bankAccountDetails?.accountNumber || "",
          bankName: staff.bankAccountDetails?.bankName || "",
          ifsc: staff.bankAccountDetails?.ifsc || "",
        },
      });
      setFieldErrors({});
    }
  }, [staff]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) setFieldErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    const field = name.replace("address.", "");
    setFormData((prev) => ({
      ...prev,
      address: { ...prev.address, [field]: value },
    }));
  };

  const handleBankDetailsChange = (e) => {
    const { name, value } = e.target;
    const field = name.replace("bankAccountDetails.", "");
    setFormData((prev) => ({
      ...prev,
      bankAccountDetails: { ...prev.bankAccountDetails, [field]: value },
    }));
    if (fieldErrors[field]) setFieldErrors((prev) => ({ ...prev, [field]: null }));
  };

  const validate = () => {
    const errors = {};
    if (!formData.firstName?.trim()) errors.firstName = "First name is required";
    else if (formData.firstName.trim().length < 2)
      errors.firstName = "First name must be at least 2 characters";
    else if (!/^[a-zA-Z\s]+$/.test(formData.firstName.trim()))
      errors.firstName = "First name should only contain letters";
    if (formData.middleName?.trim() && !/^[a-zA-Z\s]+$/.test(formData.middleName.trim()))
      errors.middleName = "Middle name should only contain letters";
    if (formData.lastName?.trim() && !/^[a-zA-Z\s]+$/.test(formData.lastName.trim()))
      errors.lastName = "Last name should only contain letters";
    if (!formData.staffId?.trim()) errors.staffId = "Staff ID is required";
    if (formData.email?.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) errors.email = "Please enter a valid email";
    }
    if (formData.phoneNumber?.trim() && !/^[0-9]{10}$/.test(formData.phoneNumber.replace(/\D/g, "")))
      errors.phoneNumber = "Phone must be 10 digits";
    const bank = formData.bankAccountDetails;
    if (bank.accountHolderName?.trim()) {
      if (!/^[a-zA-Z\s]+$/.test(bank.accountHolderName.trim()))
        errors.accountHolderName = "Account holder name should only contain letters";
      else if (bank.accountHolderName.trim().length < 2)
        errors.accountHolderName = "Account holder name must be at least 2 characters";
    }
    if (bank.accountNumber?.trim()) {
      if (!/^\d+$/.test(bank.accountNumber.trim()))
        errors.accountNumber = "Account number should only contain digits";
      else if (bank.accountNumber.trim().length < 9)
        errors.accountNumber = "Account number must be at least 9 digits";
      else if (bank.accountNumber.trim().length > 18)
        errors.accountNumber = "Account number cannot exceed 18 digits";
    }
    if (bank.bankName?.trim() && !/^[a-zA-Z\s]+$/.test(bank.bankName.trim()))
      errors.bankName = "Bank name should only contain letters";
    if (bank.ifsc?.trim() && !/^[A-Z]{4}0[A-Z0-9]{6}$/i.test(bank.ifsc.trim()))
      errors.ifsc = "Invalid IFSC code (e.g. SBIN0001234)";
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!staff?._id || !validate()) return;
    setSubmitting(true);
    try {
      const updates = {
        firstName: formData.firstName.trim(),
        middleName: formData.middleName?.trim() || "",
        lastName: formData.lastName?.trim() || "",
        staffId: formData.staffId?.trim() || undefined,
        email: formData.email?.trim() || "",
        phoneNumber: formData.phoneNumber?.trim() ? formData.phoneNumber.replace(/\D/g, "") : null,
        birthday: formData.birthday || undefined,
        bloodGroup: formData.bloodGroup || undefined,
        gender: formData.gender || undefined,
        address: formData.address,
        // Staff-specific fields
        dateOfJoining: formData.dateOfJoining || null,
        maritalStatus: formData.maritalStatus || "",
        panNumber: formData.panNumber?.trim() || null,
        epf: formData.epf?.trim() || null,
        basicSalary: formData.basicSalary ? Number(formData.basicSalary) : null,
        allowance: formData.allowance ? Number(formData.allowance) : null,
        teacherType: formData.teacherType || "",
        contractType: formData.contractType?.trim() || null,
        status: formData.status || "active",
        bankAccountDetails: {
          accountHolderName: formData.bankAccountDetails.accountHolderName || null,
          accountNumber: formData.bankAccountDetails.accountNumber || null,
          bankName: formData.bankAccountDetails.bankName || null,
          ifsc: formData.bankAccountDetails.ifsc || null,
        },
      };
      await onUpdate(staff._id, updates);
      onClose();
    } catch (err) {
      toast.error(err?.message || "Failed to update");
    } finally {
      setSubmitting(false);
    }
  };

  if (!staff) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="flex h-[100dvh] w-[100vw] max-w-none flex-col gap-0 overflow-hidden rounded-none border-0 p-0 sm:h-auto sm:max-h-[90vh] sm:w-[calc(100vw-2rem)] sm:max-w-4xl sm:rounded-lg sm:border">
        <DialogHeader className="shrink-0 border-b px-4 py-4 sm:px-6">
          <DialogTitle>Edit Staff Member</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6">
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            <div>
              <Label>First Name *</Label>
              <Input
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="First name"
              />
              {fieldErrors.firstName && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors.firstName}</p>
              )}
            </div>
            <div>
              <Label>Middle Name</Label>
              <Input
                name="middleName"
                value={formData.middleName}
                onChange={handleChange}
                placeholder="Middle name"
              />
              {fieldErrors.middleName && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors.middleName}</p>
              )}
            </div>
            <div>
              <Label>Last Name *</Label>
              <Input
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Last name"
              />
              {fieldErrors.lastName && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors.lastName}</p>
              )}
            </div>
              </div>

          <div>
            <Label>Staff ID *</Label>
            <Input
              name="staffId"
              value={formData.staffId}
              onChange={handleChange}
              placeholder="e.g. ST001 (used for login)"
            />
            {fieldErrors.staffId && (
              <p className="text-red-500 text-xs mt-1">{fieldErrors.staffId}</p>
            )}
          </div>

          <div>
            <Label>Email</Label>
            <Input
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="email@example.com"
            />
            {fieldErrors.email && (
              <p className="text-red-500 text-xs mt-1">{fieldErrors.email}</p>
            )}
          </div>

          <div>
            <Label>Phone Number</Label>
            <Input
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              placeholder="10 digits"
              maxLength={10}
            />
            {fieldErrors.phoneNumber && (
              <p className="text-red-500 text-xs mt-1">{fieldErrors.phoneNumber}</p>
            )}
          </div>

          <div>
            <Label>Birthday</Label>
            <Input
              name="birthday"
              type="date"
              value={formData.birthday}
              onChange={handleChange}
              max={new Date().toISOString().split("T")[0]}
            />
          </div>

          <div>
            <Label>Blood Group</Label>
            <select
              name="bloodGroup"
              value={formData.bloodGroup}
              onChange={handleChange}
              className="w-full h-10 px-3 rounded-md border border-input bg-background"
            >
              <option value="">Select</option>
              {["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-", "Unknown"].map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
          </div>

          <div>
            <Label>Gender</Label>
            <select
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              className="w-full h-10 px-3 rounded-md border border-input bg-background"
            >
              <option value="">Select</option>
              {["Male", "Female", "Other"].map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
          </div>

          <div>
            <Label>Address</Label>
            <div className="space-y-2">
              <Input
                name="address.street"
                placeholder="Street"
                value={formData.address.street}
                onChange={handleAddressChange}
              />
              <Input
                name="address.city"
                placeholder="City"
                value={formData.address.city}
                onChange={handleAddressChange}
              />
              <Input
                name="address.state"
                placeholder="State"
                value={formData.address.state}
                onChange={handleAddressChange}
              />
              <Input
                name="address.pincode"
                placeholder="Pincode"
                value={formData.address.pincode}
                onChange={handleAddressChange}
                maxLength={6}
              />
            </div>
          </div>

          {/* Staff-Specific Fields */}
              <div className="border-t pt-4">
            <h3 className="text-lg font-semibold mb-4">Employment Details</h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <Label>Status</Label>
                <Select
                  value={formData.status || "active"}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, status: value }))}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date of Joining</Label>
                <Input
                  name="dateOfJoining"
                  type="date"
                  value={formData.dateOfJoining}
                  onChange={handleChange}
                  max={new Date().toISOString().split("T")[0]}
                />
              </div>
              <div>
                <Label>Marital Status</Label>
                <select
                  name="maritalStatus"
                  value={formData.maritalStatus}
                  onChange={handleChange}
                  className="w-full h-10 px-3 rounded-md border border-input bg-background"
                >
                  <option value="">Select</option>
                  <option value="Single">Single</option>
                  <option value="Married">Married</option>
                  <option value="Divorced">Divorced</option>
                  <option value="Widowed">Widowed</option>
                </select>
              </div>
              <div>
                <Label>Employment Type</Label>
                <select
                  name="teacherType"
                  value={formData.teacherType}
                  onChange={handleChange}
                  className="w-full h-10 px-3 rounded-md border border-input bg-background"
                >
                  <option value="">Select</option>
                  <option value="Permanent">Permanent</option>
                  <option value="Contract">Contract</option>
                  <option value="Temporary">Temporary</option>
                  <option value="Part-time">Part-time</option>
                </select>
              </div>
              <div>
                <Label>Contract Type</Label>
                <Input
                  name="contractType"
                  value={formData.contractType}
                  onChange={handleChange}
                  placeholder="e.g. Annual, Monthly"
                />
              </div>
                </div>
              </div>

              <div className="border-t pt-4">
            <h3 className="text-lg font-semibold mb-4">Financial Details</h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <Label>Basic Salary</Label>
                <Input
                  name="basicSalary"
                  type="number"
                  value={formData.basicSalary}
                  onChange={handleChange}
                  placeholder="e.g. 25000"
                />
              </div>
              <div>
                <Label>Allowance</Label>
                <Input
                  name="allowance"
                  type="number"
                  value={formData.allowance}
                  onChange={handleChange}
                  placeholder="e.g. 5000"
                />
              </div>
              <div>
                <Label>PAN Number</Label>
                <Input
                  name="panNumber"
                  value={formData.panNumber}
                  onChange={handleChange}
                  placeholder="e.g. ABCDE1234F"
                  maxLength={10}
                />
              </div>
              <div>
                <Label>EPF Number</Label>
                <Input
                  name="epf"
                  value={formData.epf}
                  onChange={handleChange}
                  placeholder="e.g. EPF123456789"
                />
              </div>
                </div>
              </div>

              <div className="border-t pt-4">
            <h3 className="text-lg font-semibold mb-4">Bank Account Details</h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <Label>Account Holder Name</Label>
                <Input
                  name="bankAccountDetails.accountHolderName"
                  value={formData.bankAccountDetails.accountHolderName}
                  onChange={handleBankDetailsChange}
                  placeholder="Account holder name"
                />
                {fieldErrors.accountHolderName && <p className="text-red-500 text-xs mt-1">{fieldErrors.accountHolderName}</p>}
              </div>
              <div>
                <Label>Account Number</Label>
                <Input
                  name="bankAccountDetails.accountNumber"
                  value={formData.bankAccountDetails.accountNumber}
                  onChange={handleBankDetailsChange}
                  placeholder="Bank account number"
                />
                {fieldErrors.accountNumber && <p className="text-red-500 text-xs mt-1">{fieldErrors.accountNumber}</p>}
              </div>
              <div>
                <Label>Bank Name</Label>
                <Input
                  name="bankAccountDetails.bankName"
                  value={formData.bankAccountDetails.bankName}
                  onChange={handleBankDetailsChange}
                  placeholder="Bank name"
                />
                {fieldErrors.bankName && <p className="text-red-500 text-xs mt-1">{fieldErrors.bankName}</p>}
              </div>
              <div>
                <Label>IFSC Code</Label>
                <Input
                  name="bankAccountDetails.ifsc"
                  value={formData.bankAccountDetails.ifsc}
                  onChange={handleBankDetailsChange}
                  placeholder="e.g. SBIN0001234"
                />
                {fieldErrors.ifsc && <p className="text-red-500 text-xs mt-1">{fieldErrors.ifsc}</p>}
              </div>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="shrink-0 border-t px-4 py-4 sm:px-6">
            <Button type="button" variant="outline" onClick={onClose} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button type="submit" disabled={submitting} className="w-full sm:w-auto">
              {submitting ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditStaffModal;
